import { useState, useEffect } from "react";

function formatPhone(num) {
  if (!num) return "Unknown";
  const d = num.replace(/\D/g, "");
  const n = d.startsWith("1") ? d.slice(1) : d;
  if (n.length === 10) return `(${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`;
  return num;
}

function formatDuration(secs) {
  if (!secs || secs === 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
  });
}

const STATUS_COLORS = {
  completed: "var(--ok)",
  active: "#f59e0b",
  failed: "var(--err)",
  initiated: "var(--tx3)",
  pending: "var(--tx3)",
};

export default function CallHistory({ calls, loading }) {
  const [expanded, setExpanded] = useState(null);

  if (loading) {
    return (
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
          <div style={{width:24,height:24,border:"2.5px solid var(--s3)",borderTopColor:"var(--ac)",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
          <span style={{fontSize:12,color:"var(--tx3)",fontFamily:"var(--mono)"}}>Loading calls...</span>
        </div>
      </div>
    );
  }

  if (!calls || calls.length === 0) {
    return (
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12,textAlign:"center"}}>
          <div style={{width:48,height:48,borderRadius:14,background:"var(--s2)",border:"1px solid var(--s3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--tx3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/>
            </svg>
          </div>
          <div style={{fontSize:14,fontWeight:600,color:"var(--tx2)"}}>No calls yet</div>
          <div style={{fontSize:12,color:"var(--tx3)",lineHeight:1.5}}>Your call history will appear here<br/>after your first call.</div>
        </div>
      </div>
    );
  }

  // Group calls by date
  const grouped = {};
  calls.forEach(c => {
    const date = new Date(c.created_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(c);
  });

  return (
    <div style={{flex:1,overflowY:"auto",padding:"0 16px 32px",display:"flex",flexDirection:"column",gap:4}}>
      {Object.entries(grouped).map(([date, dayCalls]) => (
        <div key={date}>
          <div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.08em",padding:"12px 0 6px",textTransform:"uppercase",fontWeight:700}}>{date}</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {dayCalls.map(call => {
              const isExpanded = expanded === call.id;
              return (
                <button key={call.id} onClick={() => setExpanded(isExpanded ? null : call.id)}
                  style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:14,padding:"12px 14px",textAlign:"left",width:"100%",transition:"all 0.15s",display:"flex",flexDirection:"column",gap:isExpanded?10:0}}>

                  {/* Main row */}
                  <div style={{display:"flex",alignItems:"center",gap:12,width:"100%"}}>
                    {/* Status dot */}
                    <div style={{width:8,height:8,borderRadius:"50%",background:STATUS_COLORS[call.status]||"var(--tx3)",flexShrink:0}}/>

                    {/* Phone + time */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:"var(--tx)",fontFamily:"var(--mono)"}}>{formatPhone(call.phone_number)}</div>
                      <div style={{fontSize:11,color:"var(--tx3)"}}>{timeAgo(call.created_at)}</div>
                    </div>

                    {/* Duration + minutes */}
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--tx)",fontFamily:"var(--mono)"}}>{formatDuration(call.duration_seconds)}</div>
                      <div style={{fontSize:10,color:"var(--tx3)",fontFamily:"var(--mono)"}}>{call.billed_minutes || 0} min billed</div>
                    </div>

                    {/* Chevron */}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--tx3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{flexShrink:0,transition:"transform 0.2s",transform:isExpanded?"rotate(90deg)":"rotate(0)"}}>
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{borderTop:"1px solid var(--s3)",paddingTop:10,display:"flex",flexDirection:"column",gap:6,animation:"fadeIn 0.2s ease"}}>
                      <DetailRow label="STARTED" value={formatDate(call.started_at || call.created_at)}/>
                      {call.ended_at && <DetailRow label="ENDED" value={formatDate(call.ended_at)}/>}
                      <DetailRow label="STATUS" value={call.status?.toUpperCase()} color={STATUS_COLORS[call.status]}/>
                      <DetailRow label="MODE" value={(call.mode || "manual").toUpperCase()}/>
                      {call.is_interstate !== null && (
                        <DetailRow label="ROUTE" value={`${call.caller_state || "?"} → ${call.called_state || "?"} (${call.is_interstate ? "interstate" : "intrastate"})`}/>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailRow({ label, value, color }) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <span style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.08em",fontWeight:700}}>{label}</span>
      <span style={{fontSize:11,fontFamily:"var(--mono)",color:color||"var(--tx2)"}}>{value}</span>
    </div>
  );
}
